import { LoaderCircleIcon, UploadIcon } from 'lucide-react'
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import API from '@/lib/api'
import { useAuth } from '@/lib/auth'

export interface ImageUploadProps {
  endpoint: string
  label: string
  preview?: React.ReactNode
  onUploadSuccess?: () => void
  onUploadError?: (error: Error) => void
}

export const ImageUpload = ({
  endpoint,
  label,
  preview,
  onUploadSuccess,
  onUploadError,
  ...rest
}: ImageUploadProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'settings',
  })
  const { t: tg } = useTranslation(undefined, {
    keyPrefix: 'generic',
  })

  useAuth({
    middleware: 'auth',
    redirectTo: '/auth/login',
    tmp: 'ImageUpload',
  })

  const [isUploading, setIsUploading] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const previewUrl = useMemo(
    () => (selectedFile ? URL.createObjectURL(selectedFile) : null),
    [selectedFile],
  )

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    }
  }, [previewUrl])

  const handleFileSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setSelectedFile(file)
  }

  const triggerFileSelect = () => {
    fileInputRef.current?.click()
  }

  const triggerUpload = () => {
    setIsUploading(true)

    API.post(
      endpoint,
      {
        file: selectedFile,
      },
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          // Authorization: `Bearer ${session!.token}`,
        },
      },
    )
      .then(() => {
        setSelectedFile(null)
        onUploadSuccess?.()
      })
      .catch((error) => {
        onUploadError?.(error as Error)
      })
      .finally(() => {
        setIsUploading(false)
      })
  }

  return (
    <div className="grid gap-4" {...rest}>
      <input
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        ref={fileInputRef}
        className="hidden"
      />

      <div className="grid gap-4 sm:grid-cols-2">
        {preview}

        <button
          type="button"
          onClick={triggerFileSelect}
          className="flex min-h-40 flex-col items-center justify-center gap-2 rounded-lg border border-dashed p-6 text-center transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
        >
          {previewUrl ? (
            <>
              <img
                src={previewUrl}
                alt={t('invoicing.signature.newSignature')}
                className="max-h-28 object-contain"
              />
              <span className="text-xs text-muted-foreground">
                {selectedFile?.name}
              </span>
            </>
          ) : (
            <>
              <UploadIcon className="size-5 text-muted-foreground" />
              <span className="text-sm font-medium">{label}</span>
              <span className="text-xs text-muted-foreground">
                PNG nebo JPG, ideálně s průhledným pozadím
              </span>
            </>
          )}
        </button>
      </div>

      <div className="flex items-center gap-2">
        <Button
          type="button"
          onClick={triggerUpload}
          disabled={isUploading || !selectedFile}
        >
          {isUploading ? (
            <>
              <LoaderCircleIcon className="animate-spin" />
              {t('invoicing.signature.uploadButton')}
            </>
          ) : (
            tg('uploadFile')
          )}
        </Button>
        {selectedFile && !isUploading && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setSelectedFile(null)
              if (fileInputRef.current) fileInputRef.current.value = ''
            }}
          >
            Zrušit výběr
          </Button>
        )}
      </div>
    </div>
  )
}
